import { RecruitingInterviewDetail, RecruitingInterviewQuestion } from '../../../../store/exam/types';
import { selectProfileData } from '../../../../store/workspace/selectors';
import { getProfileDataThunk } from '../../../../store/workspace/thunks';
import { RecordedChunk } from '../../../../utils/media/ChunkedRecorder';
import { useVideoCapture } from '../../../../hooks/useVideoCapture';
import { uploadFileThunk } from '../../../../store/general/thunks';
import { useDispatch, useSelector } from '../../../../store/store';
import { setNotification } from '../../../../store/general/slice';
import { getUserInfoThunk } from '../../../../store/auth/thunks';
import { setExitExamModal } from '../../../../store/exam/slice';
import { selectUserId } from '../../../../store/auth/selectors';
import { useCountDown } from '../../../../hooks/useCountDown';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import {
    getRecruitingInterviewDataThunk,
    scoreRecruitingInterviewThunk,
} from '../../../../store/exam/thunks';
import {
    ANSWER_TIME,
    ERROR_MESSAGE,
    INTERVIEW_SUMMARY,
    INTERVIEW_EXAM_ID,
    MEDIA_DENIED_MESSAGE,
} from '../constants';
import {
    CurrentQuestion,
    InterviewPhase,
    InterviewRequest,
    MediaPermission,
    InterviewRouteState,
} from '../types';

/**
 * Drives a recruiting interview: fetches one question at a time, keeps the per-question
 * countdown and the recorded answer in step with it, and reports how long it all took.
 */
export const useRecruitingInterview = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { state } = useLocation();
    const userId = useSelector(selectUserId);
    const profile = useSelector(selectProfileData);
    const { counter, pause, restart } = useCountDown();
    const { jobId, action } = (state || {}) as InterviewRouteState;

    const [answeredTime, setAnsweredTime] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [questionNumber, setQuestionNumber] = useState(1);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [phase, setPhase] = useState<InterviewPhase>('KYC');
    const [mediaPermission, setMediaPermission] = useState<MediaPermission>('unknown');
    const [detail, setDetail] = useState<RecruitingInterviewDetail | null>(null);
    const [interview, setInterview] = useState<RecruitingInterviewQuestion | null>(null);

    const isLastQuestion = interview?.countQuestions === questionNumber;

    // Set synchronously: the countdown running out and a click on "Next question" can
    // land in the same tick, and only one of the two may move the interview on.
    const advancingRef = useRef(false);

    const question: CurrentQuestion = {
        number: questionNumber,
        text: interview?.question.question || '',
        total: interview?.countQuestions || 0,
    };

    const uploadChunk = async ({ file, fileName }: RecordedChunk) => {
        if (!userId || !jobId) return;

        await dispatch(uploadFileThunk({
            file,
            callback: setUploadProgress,
            fileName: `${userId}/${jobId}/${INTERVIEW_EXAM_ID}/${fileName}`,
        }));
    };

    const {
        webcamRef,
        nextChunk,
        stopCapture,
        startCapture,
        setChunkIndex,
    } = useVideoCapture({
        // One file per question, named after the question it holds the answer to.
        getFileName: (index, extension) => `${index}.${extension}`,
        onChunk: uploadChunk,
    });

    const notifyError = () => {
        dispatch(setNotification([ERROR_MESSAGE, 'error']));
    };

    const fetchInterview = <T,>(body: InterviewRequest): Promise<T> =>
        dispatch(getRecruitingInterviewDataThunk(body)).unwrap();

    /**
     * The interview is recorded, so both devices have to be granted before it can start.
     * The stream is released again straight away — the interview screen opens its own.
     */
    const requestMediaPermission = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
            stream.getTracks().forEach(track => track.stop());
            setMediaPermission('granted');
        } catch {
            setMediaPermission('denied');
            dispatch(setNotification([MEDIA_DENIED_MESSAGE, 'error']));
        }
    };

    useEffect(() => {
        if (!jobId) navigate('/workspace');
        if (!userId) dispatch(getUserInfoThunk());
        if (!profile) dispatch(getProfileDataThunk());
    }, []);

    useEffect(() => {
        // Everything the entrance screen shows, asked for the moment it opens.
        if (phase !== 'PreInterview' || !jobId) return;

        requestMediaPermission();
        fetchInterview<RecruitingInterviewDetail>({ jobId, pre: INTERVIEW_SUMMARY })
            .then(setDetail)
            .catch(notifyError);
    }, [phase]);

    useEffect(() => {
        // Out of time — close this answer and move on to the next question.
        if (counter === 0) nextQuestion();
    }, [counter]);

    /**
     * Hands the interview in for scoring. The answers are already uploaded by this point,
     * so a failure here costs nothing but the report — hence the screen offering a retry.
     */
    const submitInterview = async (length: number) => {
        try {
            await dispatch(scoreRecruitingInterviewThunk({
                jobId,
                length,
                interviewId: interview?.id,
            })).unwrap();

            setPhase('InterviewEnd');
        } catch {
            setPhase('SubmitFailed');
            notifyError();
        }
    };

    /** Ends the interview, holding the applicant until the last answer is uploaded. */
    const endInterview = async (length: number) => {
        setPhase('CollectingData');
        // Stay on the "collecting your data" screen until the last chunk is uploaded.
        await stopCapture();
        await submitInterview(length);
    };

    const retrySubmit = async () => {
        setIsLoading(true);
        // `answeredTime` is the length the failed attempt reported: every question has
        // been answered by now, so nothing has been added to it since.
        await submitInterview(answeredTime);
        setIsLoading(false);
    };

    const startInterview = async () => {
        if (!jobId) return;
        setIsLoading(true);

        try {
            const body = await fetchInterview<RecruitingInterviewQuestion>({
                jobId,
                pre: 1,
                action,
            });

            setInterview(body);

            if (action === 'resume' && body.questionNumber) {
                // A resumed interview picks up mid-way, so number the recordings after
                // the question they cover instead of restarting from one.
                setChunkIndex(body.questionNumber);
                setQuestionNumber(body.questionNumber);
            }

            startCapture();
            setPhase('Interview');
            restart(ANSWER_TIME);
        } catch {
            notifyError();
        } finally {
            setIsLoading(false);
        }
    };

    const nextQuestion = async () => {
        if (!jobId || !interview || advancingRef.current) return;
        advancingRef.current = true;
        pause();

        // The countdown stays paused from here, so a question that takes more than one
        // attempt to move on from still has its time counted exactly once.
        const length = answeredTime + (ANSWER_TIME - counter);

        if (isLastQuestion) {
            // Kept for the retry screen, which reports the same length again.
            setAnsweredTime(length);
            endInterview(length);
            return;
        }

        setIsLoading(true);

        try {
            const body = await fetchInterview<RecruitingInterviewQuestion>({
                jobId,
                pre: questionNumber + 1,
            });

            // Close this question's file and carry straight on, so the applicant stays
            // on camera between questions. Rotating only once the next question is in
            // hand keeps every file numbered after the question it holds the answer to.
            nextChunk();
            setAnsweredTime(length);
            setInterview(body);
            setQuestionNumber(prev => prev + 1);
            restart(ANSWER_TIME);
        } catch {
            notifyError();
        } finally {
            setIsLoading(false);
            // Released only on this path: once the last answer is in, there is nothing
            // left to advance to.
            advancingRef.current = false;
        }
    };

    return {
        jobId,
        phase,
        action,
        userId,
        detail,
        profile,
        counter,
        question,
        webcamRef,
        isLoading,
        uploadProgress,
        mediaPermission,
        retrySubmit,
        nextQuestion,
        startInterview,
        onEnableMedia: requestMediaPermission,
        exitExam: () => dispatch(setExitExamModal(true)),
        onKYCSuccess: () => setPhase('PreInterview'),
    };
};
